import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, useTheme } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EmailBlock } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { RowActions } from './RowActions';

// Wrapper component to suppress findDOMNode warning from react-quill
// This is a known issue with react-quill library
const QuillWrapper = forwardRef<ReactQuill, React.ComponentProps<typeof ReactQuill>>((props, ref) => {
  const quillRef = useRef<ReactQuill>(null);
  
  useImperativeHandle(ref, () => quillRef.current as ReactQuill);

  // Suppress findDOMNode warning in development
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.includes?.('findDOMNode is deprecated')) {
        return;
      }
      originalError.apply(console, args);
    };
    return () => {
      console.error = originalError;
    };
  }, []);

  return <ReactQuill ref={quillRef} {...props} />;
});

export const ParagraphBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}> = ({ block, isSelected, onSelect, onChange: _onChange, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  return (
    <Box sx={common} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onSelect}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Paragraph : {block.title || 'Paragraph'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
    </Box>
  );
};

export const ParagraphBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  const theme = useTheme();
  const quillRef = React.useRef<ReactQuill>(null);
  
  if (draft.type !== 'paragraph') return null;

  // Register custom font size format for Quill to handle numeric sizes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const Quill = require('react-quill').Quill;
        const SizeStyle = Quill.import('attributors/style/size');
        SizeStyle.whitelist = null; // Allow any size value
        Quill.register(SizeStyle, true);
      } catch (error) {
        console.error('Error registering Quill size format:', error);
      }
    }
  }, []);

  // Memoize the toolbar modules configuration
  const quillModules = React.useMemo(() => {
    // Custom handlers for font size increase/decrease
    const increaseFontSize = () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (!selection || selection.length === 0) {
        // If no selection, just return
        return;
      }
      
      const format = quill.getFormat(selection);
      const currentSize = format.size || '16px';
      const sizeValue = parseInt(currentSize) || 16;
      const newSize = `${sizeValue + 2}px`;
      
      quill.format('size', newSize);
    };

    const decreaseFontSize = () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (!selection || selection.length === 0) {
        // If no selection, just return
        return;
      }
      
      const format = quill.getFormat(selection);
      const currentSize = format.size || '16px';
      const sizeValue = parseInt(currentSize) || 16;
      const newSize = sizeValue > 8 ? `${sizeValue - 2}px` : '8px';
      
      quill.format('size', newSize);
    };

    // Custom handler for hyperlink
    const insertLink = () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      
      const selection = quill.getSelection();
      if (!selection) return;
      
      // Get current link value if text is already a link
      const currentFormat = quill.getFormat(selection);
      const currentLink = currentFormat.link || '';
      
      const url = prompt('Enter the URL:', currentLink);
      if (url) {
        quill.format('link', url);
      } else if (url === '') {
        // Remove link if user clears the input
        quill.format('link', false);
      }
    };

    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['font-increase', 'font-decrease'],
          ['link'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['clean']
        ],
        handlers: {
          'font-increase': increaseFontSize,
          'font-decrease': decreaseFontSize,
          'link': insertLink
        }
      },
    };
  }, []);

  const quillFormats = React.useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'size', 'color', 'background', 'align', 'list', 'bullet', 'link'
  ], []);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Edit Paragraph</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Title</Typography>
          <input value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 12 }} />

          <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Text</Typography>
          <Box sx={{ mb: 2 }}>
            <QuillWrapper
              ref={quillRef}
              theme="snow"
              value={draft.content || ''}
              onChange={(value) => setDraft({ ...draft, content: value })}
              modules={quillModules}
              formats={quillFormats}
              style={{ height: '200px' }}
            />
            <style>{`
              .ql-toolbar .ql-stroke {
                stroke: ${theme.palette.secondary.main};
              }
              .ql-toolbar .ql-fill {
                fill: ${theme.palette.secondary.main};
              }
              .ql-toolbar .ql-picker-label {
                color: ${theme.palette.secondary.main};
              }
              .ql-toolbar button:hover .ql-stroke {
                stroke: ${theme.palette.secondary.light};
              }
              .ql-toolbar button:hover .ql-fill {
                fill: ${theme.palette.secondary.light};
              }
              .ql-toolbar button.ql-active .ql-stroke {
                stroke: ${theme.palette.secondary.main};
              }
              .ql-toolbar button.ql-active .ql-fill {
                fill: ${theme.palette.secondary.main};
              }
              .ql-toolbar .ql-picker-options {
                background-color: ${theme.palette.background.paper};
                border: 1px solid ${theme.palette.divider};
              }
              .ql-toolbar .ql-picker-item {
                color: ${theme.palette.text.primary};
              }
              .ql-toolbar .ql-picker-item:hover {
                color: ${theme.palette.secondary.main};
              }
              /* Hide any unwanted buttons */
              .ql-toolbar .ql-script,
              .ql-toolbar .ql-indent,
              .ql-toolbar .ql-outdent {
                display: none !important;
              }
              /* Custom button styles */
              .ql-toolbar .ql-font-increase::before {
                content: 'A+';
                font-weight: bold;
                font-size: 14px;
              }
              .ql-toolbar .ql-font-decrease::before {
                content: 'A-';
                font-weight: bold;
                font-size: 14px;
              }
              .ql-toolbar button.ql-font-increase,
              .ql-toolbar button.ql-font-decrease {
                width: 28px !important;
              }
            `}</style>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};


